import { Link, useParams } from 'react-router-dom'
import { BrandLogo } from '../../shared/BrandLogo'
import '../web-test-motion.css'
import '../web-test-hero.css'
import '../web-test-legal.css'
import { WebsiteThemeToggle } from '../WebsiteThemeToggle'
import { portalLinks } from '../portal-links'
import { HERO_RISK_NOTICE } from '../risk-copy'
import { SeoHead } from '../seo/SeoHead'
import { defaultPageGraph } from '../seo/jsonld'
import { getPageSeo } from '../seo/pages'
import { trackClientLogin, trackOpenAccount, trackRiskDisclosure } from '../../analytics/track'
import { BLOG_POSTS, getBlogPost, type BlogBlock } from './posts'
import { WebTestNotFoundPage } from '../WebTestNotFoundPage'

function BlogHeader() {
  return (
    <header className="wl-header">
      <div className="wl-header__inner">
        <BrandLogo className="wt-logo" tone="light" to="/" />
        <nav className="wl-header__nav" aria-label="Secondary">
          <Link to="/blog">Blog</Link>
          <Link to="/about">About</Link>
          <Link to="/support">Support</Link>
        </nav>
        <div className="wl-header__actions">
          <WebsiteThemeToggle />
          <a
            className="wt-btn wt-btn--ghost"
            href={portalLinks.login}
            onClick={() => trackClientLogin('blog')}
          >
            Sign In
          </a>
          <a
            className="wt-btn wt-btn--primary"
            href={portalLinks.register}
            onClick={() => trackOpenAccount('blog')}
          >
            Open Live Account
          </a>
        </div>
      </div>
    </header>
  )
}

function Block({ block }: { block: BlogBlock }) {
  if (block.type === 'h2') {
    return <h2 style={{ fontSize: '1.15rem', margin: '0 0 10px', color: 'var(--wt-navy)' }}>{block.text}</h2>
  }
  if (block.type === 'p') {
    return <p style={{ margin: '0 0 12px', color: 'var(--wt-muted)', lineHeight: 1.65 }}>{block.text}</p>
  }
  if (block.type === 'ul') {
    return (
      <ul style={{ margin: '0 0 14px', paddingLeft: '1.2rem', color: 'var(--wt-muted)', lineHeight: 1.65 }}>
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    )
  }
  return (
    <ol style={{ margin: '0 0 14px', paddingLeft: '1.2rem', color: 'var(--wt-muted)', lineHeight: 1.65 }}>
      {block.items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ol>
  )
}

export function BlogIndexPage() {
  const seo = getPageSeo('/blog')
  if (!seo) return null

  return (
    <div className="wt-page wl-page">
      <SeoHead path="/blog" jsonLd={defaultPageGraph(seo)} />
      <BlogHeader />
      <main className="wl-main">
        <div className="wl-inner">
          <p className="wl-eyebrow">Learn</p>
          <h1 className="wl-title">{seo.h1}</h1>
          <p className="wl-section" style={{ color: 'var(--wt-muted)' }}>
            {seo.summary}
          </p>
          <p className="wl-section" style={{ color: 'var(--wt-muted)', fontSize: '0.95rem' }}>
            {HERO_RISK_NOTICE}{' '}
            <Link to="/risk-disclosure" onClick={() => trackRiskDisclosure('blog')}>
              Read the Risk Disclosure
            </Link>
            .
          </p>

          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {BLOG_POSTS.map((post) => (
              <li key={post.slug} className="wl-section" style={{ borderTop: '1px solid var(--wt-border)', paddingTop: 20 }}>
                <p className="wl-eyebrow" style={{ marginBottom: 6 }}>
                  {post.eyebrow}
                </p>
                <h2 style={{ fontSize: '1.25rem', margin: '0 0 8px', color: 'var(--wt-navy)' }}>
                  <Link to={post.path} style={{ color: 'inherit', textDecoration: 'none' }}>
                    {post.title}
                  </Link>
                </h2>
                <p style={{ margin: '0 0 10px', color: 'var(--wt-muted)', lineHeight: 1.6 }}>{post.hook}</p>
                <p style={{ margin: 0 }}>
                  <Link to={post.path}>Read guide →</Link>
                </p>
              </li>
            ))}
          </ul>

          <p className="wl-back">
            <Link to="/">← Back to Perfect Predict</Link>
          </p>
        </div>
      </main>
    </div>
  )
}

export function BlogPostPage() {
  const { slug = '' } = useParams()
  const post = getBlogPost(slug)
  if (!post) return <WebTestNotFoundPage />

  const seo = getPageSeo(post.path)
  if (!seo) return <WebTestNotFoundPage />

  return (
    <div className="wt-page wl-page">
      <SeoHead path={post.path} jsonLd={defaultPageGraph(seo)} />
      <BlogHeader />
      <main className="wl-main">
        <div className="wl-inner">
          <p className="wl-eyebrow">{post.eyebrow}</p>
          <h1 className="wl-title">{seo.h1}</h1>
          <p className="wl-updated">{post.updatedLabel}</p>
          <p className="wl-section" style={{ color: 'var(--wt-muted)' }}>
            {post.hook}
          </p>
          <p className="wl-section" style={{ color: 'var(--wt-muted)', fontSize: '0.95rem' }}>
            {HERO_RISK_NOTICE}{' '}
            <Link to="/risk-disclosure" onClick={() => trackRiskDisclosure('blog')}>
              Read the Risk Disclosure
            </Link>
            .
          </p>

          <section className="wl-section">
            <h2 style={{ fontSize: '1.15rem', margin: '0 0 10px', color: 'var(--wt-navy)' }}>
              What you will learn
            </h2>
            <ul style={{ margin: 0, paddingLeft: '1.2rem', color: 'var(--wt-muted)', lineHeight: 1.65 }}>
              {post.learn.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          {post.body.map((block, i) => (
            <div key={`${block.type}-${i}`} className={block.type === 'h2' ? 'wl-section' : undefined}>
              <Block block={block} />
            </div>
          ))}

          <section className="wl-section">
            <h2 style={{ fontSize: '1.15rem', margin: '0 0 10px', color: 'var(--wt-navy)' }}>
              Create an account
            </h2>
            <p style={{ margin: '0 0 14px', color: 'var(--wt-muted)' }}>
              Register in the Client Portal, read the Risk Disclosure, and start with Demo if you need the interface
              before funding Live.
            </p>
            <p className="wl-back" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 0 }}>
              <a
                className="wt-btn wt-btn--primary"
                href={portalLinks.register}
                onClick={() => trackOpenAccount('blog')}
              >
                Open Live Account
              </a>
              <a
                className="wt-btn wt-btn--ghost"
                href={portalLinks.login}
                onClick={() => trackClientLogin('blog')}
              >
                Client Portal Login
              </a>
              <Link className="wt-btn wt-btn--ghost" to="/risk-disclosure" onClick={() => trackRiskDisclosure('blog')}>
                Risk Disclosure
              </Link>
            </p>
          </section>

          <nav className="wl-section" aria-label="Related pages">
            <h2 style={{ fontSize: '1.15rem', margin: '0 0 8px', color: 'var(--wt-navy)' }}>Related</h2>
            <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
              {post.related.map((item) => (
                <li key={item.to}>
                  <Link to={item.to}>{item.label}</Link>
                </li>
              ))}
              <li>
                <Link to="/blog">All blog guides</Link>
              </li>
            </ul>
          </nav>

          <p className="wl-back">
            <Link to="/blog">← Back to Blog</Link>
          </p>
        </div>
      </main>
    </div>
  )
}
